import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Gauge,
  Landmark,
  Contact,
  LogOut,
  Menu,
  X,
  HardHat,
  ScrollText,
  Banknote,
  ShieldHalf,
  ChartNoAxesCombined,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  WalletMinimal,
  Handshake,
  PackageCheck,
  SlidersHorizontal,
  MonitorCog,
  DatabaseZap,
  UsersRound,
  BellRing,
} from "lucide-react";
import { useAuth } from "../helpers/useAuth";
import { useOrganization } from "../helpers/useOrganization";

import { BrandLogo } from "./BrandLogo";
import { TestModeBanner } from "./TestModeBanner";
import { ThemeModeSwitch } from "./ThemeModeSwitch";
import { TrialExpiredDialog } from "./TrialExpiredDialog";
import { Button } from "./Button";
import { Avatar, AvatarFallback, AvatarImage } from "./Avatar";
import { useUnreadCount, useRefreshNotificationsList } from "../helpers/useNotifications";
import { useScrollToTopOnNavigate } from "../helpers/useScrollToTopOnNavigate";
import styles from "./DashboardLayout.module.css";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const COLLAPSE_KEY = "dashboardSidebarCollapsed";

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
}) => {
  const { authState, logout } = useAuth();
  const { organization } = useOrganization();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSE_KEY);
    return stored === "true";
  });

  const mainRef = useRef<HTMLElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  useScrollToTopOnNavigate(mainRef, contentContainerRef);

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;
  const refreshNotifications = useRefreshNotificationsList();

  const user = authState.type === "authenticated" ? authState.user : null;

  const navItems = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <Gauge size={20} />,
    },
    {
      label: "Properties",
      path: "/properties",
      icon: <Landmark size={20} />,
    },
    {
      label: "Tenants",
      path: "/tenants",
      icon: <Contact size={20} />,
    },
    {
      label: "Leases",
      path: "/leases",
      icon: <ScrollText size={20} />,
    },
    {
      label: "Payments",
      path: "/payments",
      icon: <Banknote size={20} />,
    },
    {
      label: "Expenses",
      path: "/expenses",
      icon: <WalletMinimal size={20} />,
    },
    {
      label: "Suppliers",
      path: "/suppliers",
      icon: <Handshake size={20} />,
    },
    {
      label: "Purchase Orders",
      path: "/purchase-orders",
      icon: <PackageCheck size={20} />,
    },
    {
      label: "Maintenance",
      path: "/maintenance",
      icon: <HardHat size={20} />,
    },
    {
      label: "Reports",
      path: "/reports",
      icon: <ChartNoAxesCombined size={20} />,
    },
    {
      label: "Bulk Import",
      path: "/import",
      icon: <DatabaseZap size={20} />,
    },
  ];

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem(COLLAPSE_KEY, String(newValue));
      return newValue;
    });
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  return (
    <div className={styles.layout}>
      {/* Mobile Header */}
      <header className={styles.mobileHeader}>
        <div className={styles.mobileHeaderLeft}>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileMenu}
            className={styles.menuButton}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
          <div className={styles.brand}>
            <BrandLogo imageClassName={styles.logoImage} />
            <span className={styles.brandName}>
              {organization?.name || "BhadaPMS"}
              <span className={styles.brandDot}>.</span>
            </span>
          </div>
        </div>
        <div className={styles.mobileHeaderRight}>
          <Link to="/dashboard/notifications" className={styles.mobileHeaderLink} onClick={refreshNotifications}>
            <BellRing size={24} />
            {unreadCount > 0 && (
              <span className={styles.mobileHeaderBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
            )}
          </Link>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${isMobileMenuOpen ? styles.sidebarOpen : ""} ${isCollapsed ? styles.sidebarCollapsed : ""}`}
        style={
          organization?.primaryColor
            ? ({ "--org-primary-color": organization.primaryColor } as React.CSSProperties)
            : undefined
        }
      >
        <div className={styles.sidebarHeader}>
          <div className={styles.brand}>
            <BrandLogo imageClassName={styles.logoImage} />
            {!isCollapsed && (
              <span className={styles.brandName}>
                {organization?.name || "BhadaPMS"}
                <span className={styles.brandDot}>.</span>
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
            {!isCollapsed && <ThemeModeSwitch className={styles.themeSwitch} />}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleCollapse}
              className={styles.collapseButton}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </Button>
          </div>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? item.label : undefined}
              >
                {item.icon}
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
          <Link
            to="/dashboard/notifications"
            className={`${styles.navItem} ${
              location.pathname.startsWith("/dashboard/notifications") ? styles.navItemActive : ""
            }`}
            onClick={() => {
              setIsMobileMenuOpen(false);
              refreshNotifications();
            }}
            title={isCollapsed ? "Notifications" : undefined}
          >
            <div className={styles.navIconWrapper}>
              <BellRing size={20} />
              {isCollapsed && unreadCount > 0 && (
                <span className={styles.navBadgeDot}>{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </div>
            {!isCollapsed && (
              <>
                <span className={styles.navItemLabel}>Notifications</span>
                {unreadCount > 0 && (
                  <span className={styles.navBadgePill}>{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
              </>
            )}
          </Link>
        </nav>

        {user?.role === "owner" && (
          <>
            <div className={styles.adminSeparator} />
            <div className={styles.adminSection}>
              <Link
                to="/admin/users"
                className={`${styles.navItem} ${
                  location.pathname === "/admin/users" ? styles.navItemActive : ""
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? "Team Management" : undefined}
              >
                <UsersRound size={20} />
                {!isCollapsed && <span>Team Management</span>}
              </Link>
              <Link
                to="/settings"
                className={`${styles.navItem} ${
                  location.pathname.startsWith("/settings") ? styles.navItemActive : ""
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? "Organization Settings" : undefined}
              >
                <SlidersHorizontal size={20} />
                {!isCollapsed && <span>Organization Settings</span>}
              </Link>
            </div>
          </>
        )}

        {user?.role === "admin" && (
          <>
            <div className={styles.adminSeparator} />
            <div className={styles.adminSection}>
              <Link
                to="/admin/organizations"
                className={`${styles.navItem} ${
                  location.pathname === "/admin/organizations" ? styles.navItemActive : ""
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? "Organizations" : undefined}
              >
                <Landmark size={20} />
                {!isCollapsed && <span>Organizations</span>}
              </Link>
              <Link
                to="/admin/users"
                className={`${styles.navItem} ${
                  location.pathname === "/admin/users" ? styles.navItemActive : ""
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? "User Management" : undefined}
              >
                <ShieldHalf size={20} />
                {!isCollapsed && <span>User Management</span>}
              </Link>
              <Link
                to="/admin/settings"
                className={`${styles.navItem} ${
                  location.pathname.startsWith("/admin/settings") ? styles.navItemActive : ""
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? "Platform Admin" : undefined}
              >
                <MonitorCog size={20} />
                {!isCollapsed && <span>Platform Admin</span>}
              </Link>
            </div>
          </>
        )}

        <div className={styles.mobileThemeRow}>
          <span className={styles.mobileThemeLabel}>Theme</span>
          <ThemeModeSwitch />
        </div>

        <div className={styles.userSection}>
          <Link
            to="/profile/settings"
            className={styles.userInfoLink}
            title={isCollapsed ? "My Settings" : undefined}
          >
            <div className={styles.userInfo}>
              <Avatar className={styles.avatar}>
                <AvatarImage src={user?.avatarUrl || undefined} />
                <AvatarFallback>
                  {user?.displayName?.substring(0, 2).toUpperCase() || "US"}
                </AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <div className={styles.userDetails}>
                  <div className={styles.userNameRow}>
                    <span className={styles.userName}>{user?.displayName}</span>
                    <ChevronDown size={14} className={styles.userNameChevron} />
                  </div>
                  <div className={styles.userRoleRow}>
                    <span className={styles.userRole}>{user?.role}</span>
                  </div>
                </div>
              )}
            </div>
          </Link>
          <Button
            variant="ghost"
            size={isCollapsed ? "icon-sm" : "sm"}
            className={styles.logoutButton}
            onClick={() => logout()}
            title="Log out"
          >
            <LogOut size={16} />
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main ref={mainRef} className={`${styles.main} ${isCollapsed ? styles.mainCollapsed : ""}`}>
        <TestModeBanner />
        <div ref={contentContainerRef} className={styles.contentContainer}>{children}</div>
      </main>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div className={styles.overlay} onClick={() => setIsMobileMenuOpen(false)} />
      )}
      
      <TrialExpiredDialog />
    </div>
  );
};